
"use client";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { SubjectStats } from "@/lib/types";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import HelpTooltip from "@/components/ui/HelpTooltip";

interface SubjectBalanceChartProps {
    biology: SubjectStats;
    chemistry: SubjectStats;
}

export default function SubjectBalanceChart({ biology, chemistry }: SubjectBalanceChartProps) {
    // We want to show the average score for each subject
    const data = [
        { name: 'Biology', value: parseFloat(biology.percentage.toFixed(1)), color: '#10b981' }, // emerald-500
        { name: 'Chemistry', value: parseFloat(chemistry.percentage.toFixed(1)), color: '#3b82f6' }, // blue-500
    ];

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg flex items-center">
                    Subject Balance
                    <HelpTooltip content="Shows the average score distribution between Biology and Chemistry. A balanced performance is ideal, but ensure both remain above the 55% passing threshold." />
                </CardTitle>
                <CardDescription>Average performance by subject</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="h-[200px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                formatter={(value: number) => [`${value}%`, 'Average Score']}
                                contentStyle={{ backgroundColor: 'hsl(var(--card))', borderRadius: '8px', border: '1px solid hsl(var(--border))' }}
                            />
                            <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="mt-4 space-y-2">
                    <div className="flex justify-between items-center text-sm">
                        <span className="flex items-center"><div className="w-3 h-3 rounded-full bg-emerald-500 mr-2" />Biology</span>
                        <span className="font-bold">{biology.percentage.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                        <span className="flex items-center"><div className="w-3 h-3 rounded-full bg-blue-500 mr-2" />Chemistry</span>
                        <span className="font-bold">{chemistry.percentage.toFixed(1)}%</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
